import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/AdminAuthContext';
import { 
  LayoutDashboard, FolderKanban, Image, MessageSquare, FileText, 
  Palette, Users, Settings, LogOut, ChevronLeft, PanelRightOpen,
  Package, ScrollText, Menu
} from 'lucide-react';
import { useState } from 'react';
import { ScrollToTop } from '@/components/ScrollToTop';

const navItems = [
  { to: '/admin/dashboard', label: 'Дашборд', icon: LayoutDashboard },
  { to: '/admin/portfolio', label: 'Портфолио', icon: FolderKanban },
  { to: '/admin/media', label: 'Медиа', icon: Image },
  { to: '/admin/cms', label: 'Страницы', icon: FileText },
  { to: '/admin/services', label: 'Услуги', icon: Package },
  { to: '/admin/leads', label: 'Заявки', icon: MessageSquare },
  { to: '/admin/design', label: 'Дизайн', icon: Palette },
  { to: '/admin/audit', label: 'Журнал', icon: ScrollText },
  { to: '/admin/users', label: 'Пользователи', icon: Users },
  { to: '/admin/settings', label: 'Настройки', icon: Settings },
];

export function AdminLayout() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  if (!user || !isAdmin) {
    return <Outlet />;
  }

  const sidebar = (
    <aside
      className={`${collapsed ? 'md:w-16' : 'md:w-60'} w-60 border-r border-zinc-800 bg-zinc-900/95 md:bg-zinc-900/50 flex flex-col transition-all duration-200 h-full`}
    >
        <div className="flex items-center justify-between border-b border-zinc-800 px-4 h-14">
          {!collapsed && (
            <span className="text-sm font-bold tracking-tight">
              SHOT <span className="text-purple-400">BRO</span>
            </span>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="text-zinc-500 hover:text-zinc-300 transition cursor-pointer p-1"
          >
            {collapsed ? <PanelRightOpen size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>

        <nav className="flex-1 space-y-1 px-2 py-4 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition min-h-[44px] ${
                  isActive 
                    ? 'bg-purple-600/10 text-purple-400 border border-purple-600/20' 
                    : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50'
                }`
              }
            >
              <item.icon size={18} />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-zinc-800 p-3">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-purple-600 flex items-center justify-center text-xs font-bold">
              {user.full_name?.charAt(0) || 'A'}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate">{user.full_name}</p>
                <p className="text-[10px] text-zinc-500 truncate">{user.email}</p>
              </div>
            )}
            <button
              onClick={handleLogout}
              className="text-zinc-500 hover:text-red-400 transition cursor-pointer"
              title="Выйти"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>
  );

  return (
    <div className="flex min-h-screen bg-zinc-950 text-zinc-100">
      <ScrollToTop />
      <div className="hidden md:flex shrink-0">{sidebar}</div>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/60 cursor-pointer"
            aria-label="Закрыть меню"
            onClick={() => setMobileOpen(false)}
          />
          <div className="relative h-full w-[min(100%,16rem)] shadow-xl">{sidebar}</div>
        </div>
      )}

      <main className="flex-1 overflow-auto min-w-0">
        <div className="flex md:hidden items-center gap-3 border-b border-zinc-800 px-4 h-14 sticky top-0 bg-zinc-950/95 backdrop-blur z-40">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="p-2 text-zinc-400 cursor-pointer min-h-[44px] min-w-[44px]"
            aria-label="Меню"
          >
            <Menu size={22} />
          </button>
          <span className="text-sm font-bold">
            SHOT <span className="text-purple-400">BRO</span> Admin
          </span>
        </div>
        <div className="p-4 md:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}